import GPNDao from '../components/gpn-snapshot/gpn-snapshot.dao';
import { GPNMatch } from '../components/gpn-snapshot/gpn-snapshot.interface';
import OPNDao from '../components/opn-snapshot/opn-snapshot.dao';
import { OPNMatch } from '../components/opn-snapshot/opn-snapshot.interface';
import PartMatchDao from '../components/part-matches/part-matches.dao';
import { PartMatch } from '../components/part-matches/part-matches.interface';
import ProductFamilyDao from '../components/product-family/product-family.dao';
import ToolDao from '../components/tool-snapshot/tool-snapshot.dao';
import { ToolMatch } from '../components/tool-snapshot/tool-snapshot.interface';
import { default as languageStrings } from './l10n/Search.json';
import LanguagePreferences from './language-preferences';
import Util from './util';

import { QuerySummary, Result, ResultListState, SearchEngine, buildBreadcrumbManager, buildCategoryFacet, buildContext, buildDidYouMean, buildQuerySummary, buildResultList, buildResultsPerPage, buildSearchBox, buildSearchStatus, buildSort, buildUrlManager, loadGenericAnalyticsActions } from '@coveo/headless';

export class SearchUpgrade {

    private static analyticsQueue: Array<{
        eventName: string,
        eventDetail: string,
        eventLabel: string,
        retryCount: number
    }> = [];
    private static headlessEngine: SearchEngine;
    private static headlessSearchTerm: string;
    private static locale: string = 'en-US';
    private static metricsSearchTerm: string;
    private static referralSource: string;

    private static SEARCH_STATE = {
        productFilter: '',
        opnMatch: { searchTerm: null, response: null },
        gpnMatch: { searchTerm: null, response: null },
        toolMatch: { searchTerm: null, response: null },
        partMatches: { searchTerm: null, response: null },
        productFamily: { productFilter: null, response: null },
        snapshotLoading: false
    }

    private static querySummaryController: QuerySummary;

    private static readonly METRICS_MAX_RETRY_COUNT = 5;
    private static readonly METRICS_RETRY_WAIT_TIME = 1250;

    private static setLocale(locale: string = 'en-US') {
        switch (locale.toLowerCase()) {
            case 'cn':
            case 'zh-cn':
                SearchUpgrade.locale = 'zh-CN';
                break;
            case 'es-mx':
                SearchUpgrade.locale = 'es-MX';
                break;
            case 'de':
            case 'de-de':
                SearchUpgrade.locale = 'de-DE';
                break;
            case 'jp':
            case 'ja-jp':
                SearchUpgrade.locale = 'ja-JP';
                break;
            case 'kr':
            case 'ko-kr':
                SearchUpgrade.locale = 'ko-KR';
                break;
            case 'fr-fr':
                SearchUpgrade.locale = 'fr-FR';
                break;
            case 'ru-ru':
                SearchUpgrade.locale = 'ru-RU';
                break;
            case 'tr-tr':
                SearchUpgrade.locale = 'tr-TR';
                break;
            case 'zh-tw':
                SearchUpgrade.locale = 'zh-TW';
                break;
            default:
                SearchUpgrade.locale = 'en-US';
                break;
        }
    }

    private static async getOPNMatch(searchTerm: string): Promise<OPNMatch> {
        searchTerm = searchTerm.trim();
        if (!SearchUpgrade.SEARCH_STATE.opnMatch || !SearchUpgrade.SEARCH_STATE.opnMatch.searchTerm || (SearchUpgrade.SEARCH_STATE.opnMatch.searchTerm !== searchTerm)) {
            const match: OPNMatch = await OPNDao.fetchOPNMatch(searchTerm, SearchUpgrade.locale);
            SearchUpgrade.SEARCH_STATE.opnMatch = { searchTerm: searchTerm, response: (match && match.opn) ? match : null };
        }
        return SearchUpgrade.SEARCH_STATE.opnMatch.response;
    }

    private static async getGPNMatch(searchTerm: string): Promise<GPNMatch> {
        searchTerm = searchTerm.trim();
        if (!SearchUpgrade.SEARCH_STATE.gpnMatch || !SearchUpgrade.SEARCH_STATE.gpnMatch.searchTerm || (SearchUpgrade.SEARCH_STATE.gpnMatch.searchTerm !== searchTerm)) {
            const match: GPNMatch = await GPNDao.fetchGPNMatch(searchTerm, SearchUpgrade.locale);
            SearchUpgrade.SEARCH_STATE.gpnMatch = { searchTerm: searchTerm, response: (match && match.gpn) ? match : null };
        }
        return SearchUpgrade.SEARCH_STATE.gpnMatch.response;
    }

    private static async getToolMatch(searchTerm: string): Promise<ToolMatch> {
        searchTerm = searchTerm.trim();
        if (!SearchUpgrade.SEARCH_STATE.toolMatch || !SearchUpgrade.SEARCH_STATE.toolMatch.searchTerm || (SearchUpgrade.SEARCH_STATE.toolMatch.searchTerm !== searchTerm)) {
            const match: ToolMatch = await ToolDao.fetchToolMatch(searchTerm, SearchUpgrade.locale);
            SearchUpgrade.SEARCH_STATE.toolMatch = { searchTerm: searchTerm, response: (match && match.toolPartNumber) ? match : null };
        }
        return SearchUpgrade.SEARCH_STATE.toolMatch.response;
    }

    private static async getPartMatches(searchTerm: string): Promise<PartMatch[]> {
        searchTerm = searchTerm.trim();
        if (!SearchUpgrade.SEARCH_STATE.partMatches || !SearchUpgrade.SEARCH_STATE.partMatches.searchTerm || (SearchUpgrade.SEARCH_STATE.partMatches.searchTerm !== searchTerm)) {
            const match: PartMatch[] = await PartMatchDao.fetchPartMatches(searchTerm, SearchUpgrade.locale);
            SearchUpgrade.SEARCH_STATE.partMatches = { searchTerm: searchTerm, response: (match && match.length > 0) ? match : null };
        }
        return SearchUpgrade.SEARCH_STATE.partMatches.response;
    }

    private static async getProductFamilyUrl(productFilter: string): Promise<string> {
        productFilter = productFilter.trim();
        if (!SearchUpgrade.SEARCH_STATE.productFamily || !SearchUpgrade.SEARCH_STATE.productFamily.productFilter || (SearchUpgrade.SEARCH_STATE.productFamily.productFilter !== productFilter)) {
            const familyUrl: string = await ProductFamilyDao.searchProductFamily(productFilter, SearchUpgrade.locale);
            SearchUpgrade.SEARCH_STATE.productFamily = { productFilter: productFilter, response: (familyUrl) ? familyUrl : null };
        }
        return SearchUpgrade.SEARCH_STATE.productFamily.response;
    }

    public static async initSearchEngine() {
        // set locale value (TISearchData.website defined in sitesearch)
        const locale = (window as any)?.TISearchData?.website ?? 'en-US';
        SearchUpgrade.setLocale(locale);

        SearchUpgrade.addUseCaseListeners();

        await customElements.whenDefined("atomic-search-interface");
        await customElements.whenDefined("atomic-search-box");
        const searchInterface = document.querySelector("#search") as any;
        let headerSearchInterface = document.querySelector("#headerSearch") as any;

        await Util.waitForTrue(() => {
            headerSearchInterface = document.querySelector("#headerSearch") as any;
            return headerSearchInterface && headerSearchInterface.engine;
        }, 100, 500).catch(() => {
            console.log('no search engine found');
            return Promise.resolve();
        });

        if (headerSearchInterface.engine) {
            SearchUpgrade.headlessEngine = headerSearchInterface.engine;
            await searchInterface.initializeWithSearchEngine(headerSearchInterface.engine);
            searchInterface.analytics = SearchUpgrade.enableGDPRCompliance();

            // build context
            const searchContext = buildContext(headerSearchInterface.engine);
            const lang: string = await LanguagePreferences.resolveUserLangPref();
            console.log(`user_pref_language: ${lang}`);
            const geo = Util.getCookie('ti_geo');
            const consent = Util.getCookie('CONSENTMGR');
            const chipset = Util.getCookie('chipset');
            const session = Util.getCookie('tiSessionID');
            const bm = Util.getCookie('ti_bm');
            const pn = (window as any).utag_data['cp.utag_main__pn'];
            // default to false - value will be updated on each search in preprocessRequest hook in coveo-components
            const sqDefault = 'false';

            searchContext.set({
                "user_pref_language": lang,
                "ti_geo": geo,
                "consentmgr": consent,
                "chipset": chipset,
                "tisessionid": session,
                "ti_bm": bm,
                "utag_main__pn": pn,
                "static_query": sqDefault
            });

            const searchStatusController = buildSearchStatus(SearchUpgrade.headlessEngine);

            this.querySummaryController = buildQuerySummary(SearchUpgrade.headlessEngine);

            const breadCrumbController = buildBreadcrumbManager(SearchUpgrade.headlessEngine);
            breadCrumbController.subscribe(() => {

                if (breadCrumbController.state.hasBreadcrumbs) {
                    setTimeout(async () => {
                        const breadbox = document.querySelector('atomic-breadbox');
                        let clearAllFiltersBtn = breadbox?.shadowRoot?.querySelector('button[part="clear"]');
                        let clearFilterBreadcrumbBtns = breadbox?.shadowRoot?.querySelectorAll('button[part="breadcrumb-button"]');
                        await Util.waitForTrue(() => {
                            clearAllFiltersBtn = breadbox?.shadowRoot?.querySelector('button[part="clear"]');
                            clearFilterBreadcrumbBtns = breadbox?.shadowRoot?.querySelectorAll('button[part="breadcrumb-button"]');
                            return clearAllFiltersBtn && clearFilterBreadcrumbBtns;
                        }, 10, 100).catch(() => {
                            return Promise.resolve();
                        });
                        clearAllFiltersBtn?.addEventListener('click', async () => {
                            await SearchUpgrade.removePrefilter();
                        });
                        clearFilterBreadcrumbBtns?.forEach((btn: any) => {
                            btn.addEventListener('click', async () => {
                                await SearchUpgrade.removePrefilter();
                            });
                        });
                    }, 50);
                }
            });

            // searchBox subscription - run snapshot logic on value change
            const searchBoxController = buildSearchBox(headerSearchInterface.engine);
            searchBoxController.subscribe(async () => {
                const searchBox = document.querySelector('atomic-search-box') as any;
                if (searchBox) {
                    searchBox.redirectionUrl = undefined;
                }

                let searchTerm: string = searchBoxController.state?.value;
                const searchTermChanged = searchTerm?.localeCompare(SearchUpgrade.headlessSearchTerm) !== 0;
                searchTerm = SearchUpgrade.cleanseSearchTerm(searchTerm);

                // ! Handle PreFilter logic
                if (searchTermChanged) {
                    SearchUpgrade.handlePrefilter();
                    SearchUpgrade.setOrUpdateQueryParamWithNewValue('searchTerm', searchTerm);

                    if (searchTerm?.length > 0) {
                        // synchronize URL with cleansed searchTerm
                        const newHash = Util.setHashParam(window.location.hash, 'q', searchTerm);
                        window.location.hash = newHash;

                        SearchUpgrade.updateReflangLinksWithSearchTerm(encodeURIComponent(searchTerm));
                    }
                }
                //update ti login redirect url
                document.querySelector('ti-login')?.setAttribute('login-url', `/secure-link-forward/?gotoUrl=${encodeURIComponent(document.location.href)}`);

                SearchUpgrade.handleSnapshotDisplay(searchTerm).then(() => {
                    SearchUpgrade.headlessSearchTerm = searchTerm;
                    const state = searchStatusController?.state;
                    if (!state?.isLoading && !state?.hasError) {

                        SearchUpgrade.clearResultsArea();
                        SearchUpgrade.closeMobileSearchBox();
                        if (!state.hasResults) {
                            SearchUpgrade.displayNoResultsFound(searchTerm);
                            SearchUpgrade.noResultsCheckXref(searchTerm);
                        } else {
                            SearchUpgrade.addSmartSnippetsMetrics();
                        }

                        // trigger metrics for new searchTerm
                        const searchTermFinal = searchTerm.localeCompare(SearchUpgrade.cleanseSearchTerm(searchTerm)) === 0;
                        const searchTermChanged = searchTerm.localeCompare(this.metricsSearchTerm) !== 0;
                        if (state.firstSearchExecuted && searchTermFinal && searchTermChanged) {
                            this.metricsSearchTerm = searchTerm;
                            const nullSearch = !state.hasResults;
                            SearchUpgrade.trackSearchMetrics(searchTerm, nullSearch);
                        }

                        // trigger metrics for snapshot use cases --
                        // these are triggered here from the queue to ensure that the URL query parameter value has been updated in the URL
                        this.dequeueAnalyticsCalls();
                    }

                });
            });

            // product facet subscription - run productFamily component logic on value change
            const locale = lang?.toLowerCase()?.replace('-', '_');
            const facetControllerProducts = buildCategoryFacet(headerSearchInterface.engine, { options: { facetId: 'products', field: `familyhierarchy_${locale}` } });

            facetControllerProducts.subscribe(async () => {
                const state = facetControllerProducts?.state;
                const val = state?.activeValue?.value ?? '';
                const isChanged = val !== SearchUpgrade.SEARCH_STATE.productFilter;
                if (isChanged) {
                    SearchUpgrade.SEARCH_STATE.productFilter = val;
                    this.handleProductFilterChanged(val);
                }
            });

            if (typeof (window as any).addLanguageBundle === "function") {
                await (window as any).addLanguageBundle();
            }

            const resultListController = buildResultList(SearchUpgrade.headlessEngine);
            resultListController.subscribe(async () => {
                const state = resultListController.state;
                if (state.firstSearchExecuted && !state.hasError && !state.isLoading && state.hasResults) {
                    SearchUpgrade.managePagerVisibility();
                    SearchUpgrade.addResultListMetrics(state);
                }
                if (state.firstSearchExecuted && !state.hasError && !state.isLoading) {
                    const numResults = state.hasResults ? `${this.querySummaryController.state.total}` : '0';
                    SearchUpgrade.setOrUpdateQueryParamWithNewValue('nr', numResults);
                }

            });

            const fragment = window.location.hash.slice(1);
            const urlManager = buildUrlManager(SearchUpgrade.headlessEngine, {
                initialState: { fragment }
            });

            urlManager.subscribe(() => {

                const hash = `#${urlManager.state.fragment}`;

                if (!searchStatusController.state.firstSearchExecuted) {
                    history.replaceState(null, document.title, hash);

                    return;
                }

                history.pushState(null, document.title, hash);
            });

            window.addEventListener('hashchange', () => {
                const fragment = window.location.hash.slice(1);
                urlManager.synchronize(fragment);
            });


            const sortController = buildSort(SearchUpgrade.headlessEngine);
            sortController.subscribe(() => {
                if (searchStatusController.state.firstSearchExecuted) {
                    const eventName = 'On-site Search - Sort By';
                    const eventDetail = SearchUpgrade.SORT_METRICS[sortController.state.sortCriteria?.toLowerCase()];
                    if (eventDetail) {
                        SearchUpgrade.makeAnalyticsCall(eventName, eventDetail);
                    }
                }
            });

            const resultsPerPageController = buildResultsPerPage(SearchUpgrade.headlessEngine);
            resultsPerPageController.subscribe(() => {
                if (searchStatusController.state.firstSearchExecuted) {
                    const eventName = 'onsite search interaction';
                    const eventAction = 'page result count';
                    const eventLabel = `page result count|${resultsPerPageController.state.numberOfResults}`;
                    this.makeAnalyticsCall(eventName, eventAction, eventLabel);
                }
            });

            // metrics for did-you-mean
            const didYouMeanController = buildDidYouMean(SearchUpgrade.headlessEngine);
            didYouMeanController.subscribe(() => {
                const state = didYouMeanController.state;
                if (searchStatusController.state.firstSearchExecuted && !searchStatusController.state.isLoading && state.hasQueryCorrection) {
                    let eventAction = 'did you mean suggestion impression';
                    const originalQuery = state.originalQuery;
                    let correctedQuery = state.queryCorrection?.correctedQuery;
                    if (state.wasAutomaticallyCorrected) {
                        eventAction = 'did you mean autocorrection';
                        correctedQuery = state.wasCorrectedTo;
                    } else if (state.wasCorrectedTo) {
                        eventAction = 'did you mean suggestion click';
                        correctedQuery = state.wasCorrectedTo;
                    }
                    const eventName = 'onsite search interaction';
                    const eventLabel = `${originalQuery}|${correctedQuery}`;
                    this.makeAnalyticsCall(eventName, eventAction, eventLabel);
                }
            });

            // initilization complete - trigger first search
            searchInterface.executeFirstSearch();

            SearchUpgrade.addCoveoCustomMetricsListeners();
        }
    }

    private static cleanseSearchTerm(searchTerm: string): string {
        try {
            searchTerm = decodeURIComponent(searchTerm);
        } catch (err) {
            console.error(err);
        }
        searchTerm = searchTerm.replace(/_/g, ' ')
            .replace(/file:|ftp:|data:|http:/ig, ' ')
            .replace(/c:/g, ' ')
            .replace(/\s+/g, ' ')
            .replace(/[`~!@#$%^&*()|+\=?;'",<>\{\}\[\]]/gi, '')
            .replace(/null/ig, ' ')
            .replace(/•/ig, ' ')
            .trim();
        searchTerm = (searchTerm.length > 250) ? searchTerm.substring(0, 250) : searchTerm;
        return searchTerm?.trim();
    }

    private static async handleProductFilterChanged(productFilterSubFamily: string) {
        const productFamily = document.querySelector('ti-product-family') as HTMLTiProductFamilyElement;
        if (productFamily) {
            const familyUrl = productFilterSubFamily ? await SearchUpgrade.getProductFamilyUrl(productFilterSubFamily) : null;
            if (familyUrl) {
                const searchString = this.cleanseSearchTerm(this.headlessEngine.state.query.q);
                productFamily.style.display = '';
                productFamily.productFilter = productFilterSubFamily;
                productFamily.searchString = searchString;
                productFamily.updateData(familyUrl);
            } else {
                productFamily.style.display = 'none';
                productFamily.productFilter = '';
                productFamily.searchString = '';
                productFamily.updateData(null);
            }
        }
    }

    private static async handleSnapshotDisplay(searchTerm: string): Promise<void> {
        const searchTermChanged = searchTerm?.localeCompare(SearchUpgrade.headlessSearchTerm) !== 0;

        const firstLoadInProgress = !SearchUpgrade.headlessSearchTerm && SearchUpgrade.SEARCH_STATE.snapshotLoading === true;

        // display product extras
        const searchQuerySnapshots = document.querySelector('div.search-query-snapshots') as HTMLElement;
        if (searchTerm && searchTermChanged && !firstLoadInProgress) {

            SearchUpgrade.SEARCH_STATE.snapshotLoading = true;

            const opnSnapshot = document.querySelector('ti-opn-snapshot') as HTMLTiOpnSnapshotElement;
            const toolSnapshot = document.querySelector('ti-tool-snapshot') as HTMLTiToolSnapshotElement;
            const gpnSnapshot = document.querySelector('ti-gpn-snapshot') as HTMLTiGpnSnapshotElement;
            const partMatchList = document.querySelector('ti-part-matches') as HTMLTiPartMatchesElement;
            const productFamily = document.querySelector('ti-product-family') as HTMLTiProductFamilyElement;

            // hide all components initially
            if (searchQuerySnapshots) {
                searchQuerySnapshots.style.display = 'none';
            }
            if (opnSnapshot) {
                opnSnapshot.style.display = 'none';
                opnSnapshot.setAttribute('opn', '');
                opnSnapshot.updateData(null);
            }
            if (toolSnapshot) {
                toolSnapshot.style.display = 'none';
                toolSnapshot.setAttribute('tool-part-number', '');
                toolSnapshot.updateData(null);
            }
            if (gpnSnapshot) {
                gpnSnapshot.style.display = 'none';
                gpnSnapshot.setAttribute('gpn', '');
                gpnSnapshot.updateData(null);
            }
            if (partMatchList) {
                partMatchList.style.display = 'none';
                partMatchList.setAttribute('search-string', '');
                partMatchList.setAttribute('use-gpn-title-label', 'false');
                partMatchList.updateData(null);
            }
            if (productFamily) {
                productFamily.style.display = 'none';
                productFamily.setAttribute('product-filter', '');
                productFamily.updateData(null);
            }

            // which component(s) to display?
            if (searchTerm) {
                let opnMatchDisplayed: boolean = false;
                let gpnMatchDisplayed: boolean = false;
                let toolMatchDisplayed: boolean = false;
                let partMatchesDisplayed: boolean = false;
                let matchedGpn: string = null;
                let matchedGpnFamilyName: string = null;

                // opn match?
                const opnMatch: OPNMatch = await SearchUpgrade.getOPNMatch(searchTerm);
                let toolMatch: ToolMatch;
                let likeAvailableForPurchase: boolean = false;
                if (opnMatch && opnMatch.opnLike) {
                    opnMatch.opnLike.forEach(like => {
                        if (like.availableForPurchaseFlag === 'Y') likeAvailableForPurchase = true;
                    });
                }
                if (opnMatch && opnMatch.opnMatch && (opnMatch.opnMatch.availableForPurchaseFlag === 'Y' || likeAvailableForPurchase)) { // opn match ?
                    // display opn snapshot if searched opn is available for purchase or any one of its like parts are available for purchase
                    if (opnSnapshot) {
                        opnSnapshot.style.display = '';
                        opnSnapshot.updateData(opnMatch);
                        opnMatchDisplayed = true;
                    }
                } else {  // gpn match ?
                    const gpnMatch: GPNMatch = await SearchUpgrade.getGPNMatch(searchTerm);
                    if (gpnMatch) {
                        if (gpnSnapshot) {
                            gpnSnapshot.style.display = '';
                            gpnSnapshot.updateData(gpnMatch);
                            matchedGpn = gpnMatch.gpn;
                            matchedGpnFamilyName = gpnMatch.familyName
                            gpnMatchDisplayed = true;
                        }
                    } else {  // tool match - exact match ?
                        toolMatch = await SearchUpgrade.getToolMatch(searchTerm);
                        const toolMatchExactMatch = toolMatch && toolMatch.toolPartNumber.localeCompare(searchTerm, undefined, { sensitivity: "base" }) === 0;
                        if (toolMatchExactMatch && toolSnapshot) {
                            toolSnapshot.style.display = '';
                            toolSnapshot.toolPartNumber = searchTerm;
                            toolSnapshot.updateData(toolMatch);
                            toolMatchDisplayed = true;
                        }
                    }
                }
                if (partMatchList && !opnMatchDisplayed && !toolMatchDisplayed) { // part matches ?
                    let partMatches: PartMatch[] = await SearchUpgrade.getPartMatches(searchTerm);
                    if (partMatches?.length > 1 || (partMatches?.length === 1 && gpnMatchDisplayed)) { // if only 1 match and no snapshots, don't show
                        if (gpnMatchDisplayed && matchedGpn) {
                            partMatchList.setAttribute('use-gpn-title-label', 'true');
                            // remove exact match (already shown in GPN snapshot)
                            partMatches = partMatches.filter(part => {
                                return part.partNumber != matchedGpn;
                            });
                        }
                        if (partMatches.length > 0) {
                            partMatchList.style.display = '';
                            partMatchList.setAttribute('priority-family-name', matchedGpnFamilyName);
                            partMatchList.searchString = searchTerm;
                            partMatchList.updateData(partMatches);
                            partMatchesDisplayed = true;
                        }
                    }
                }

                if (toolSnapshot && !partMatchesDisplayed && !toolMatchDisplayed && toolMatch) { // tool snapshot - partial match
                    if (toolSnapshot) {
                        toolSnapshot.style.display = '';
                        toolSnapshot.updateData(toolMatch);
                        toolMatchDisplayed = true;
                    }
                }
            }
        }
        SearchUpgrade.SEARCH_STATE.snapshotLoading = false;

        SearchUpgrade.handleSnapshotVisibility();
    }

    private static handleSnapshotVisibility() {
        const searchQuerySnapshots = document.querySelector('div.search-query-snapshots') as HTMLElement;

        // show / hide snapshots
        if (searchQuerySnapshots) {
            const searchExtrasSnapshots = searchQuerySnapshots.querySelectorAll(':not([style*="display: none"])');
            const filtersApplied = SearchUpgrade.headlessEngine?.state?.search?.response?.facets?.some((f) => f?.values?.some((fo) => 'selected' === fo?.state || fo?.children?.length));
            searchQuerySnapshots.style.display = (filtersApplied || !searchExtrasSnapshots || !searchExtrasSnapshots.length) ? 'none' : '';
        }
    }

    private static displayNoResultsFound(searchTerm?: string): void {
        let noMatchesProdText: string;
        if (SearchUpgrade.referralSource === 'partner') {
            noMatchesProdText = languageStrings[SearchUpgrade.locale].noResultsPartnerDirectory.replace('{0}', languageStrings[SearchUpgrade.locale].partnerDirectoryUrl);
        } else {
            noMatchesProdText = languageStrings[SearchUpgrade.locale].noResults.replace('{0}', searchTerm ?? SearchUpgrade.headlessSearchTerm);
        }
        noMatchesProdText = noMatchesProdText.replace('{1}', languageStrings[SearchUpgrade.locale].refDesignUrl);
        noMatchesProdText = noMatchesProdText.replace('{2}', languageStrings[SearchUpgrade.locale].crossRefUrl);
        noMatchesProdText = noMatchesProdText.replace('{3}', languageStrings[SearchUpgrade.locale].csmUrl);
        noMatchesProdText = noMatchesProdText.replace('{4}', languageStrings[SearchUpgrade.locale].e2eUrl);

        const el = document.createElement('div');
        el.innerHTML = noMatchesProdText;
        document.querySelector('#c7').appendChild(el.firstChild);
        const s = document.createElement('style');
        s?.appendChild(document.createTextNode(".ti-search-pageContent .coveo-no-results,.ti-search-pageContent .coveo-results-per-page-no-results{display:none;}"));
        document.querySelector('#c7')?.appendChild(s);
    }

    private static clearResultsArea(): void {
        if (document.querySelector('#noMatch')) {
            document.querySelector('#noMatch').remove();
        }
    }

    private static XREF_LOCALES = ['en-US', 'ja-JP', 'zh-CN', 'ko-KR', 'de-DE', 'ru-RU'];
    private static XREF_HASH = 'supplyframe=1';

    private static async noResultsCheckXref(query: string): Promise<any> {
        if (query.length < 3) {
            return false;
        }
        // don't redirect if there is a snapshot result displayed
        let retryCount = 0;
        while (SearchUpgrade.SEARCH_STATE.snapshotLoading && retryCount < 3) {
            await Util.wait(1000);
            retryCount++;
        }
        if (document.querySelectorAll('div.search-query-snapshots :not([style*="display: none"])')?.length > 0) {
            return false;
        }

        const xrefLocale = SearchUpgrade.XREF_LOCALES.includes(SearchUpgrade.locale)
            ? SearchUpgrade.locale
            : 'en-US';

        if (await SearchUpgrade.fetchXRefCheck(query, xrefLocale)) {
            SearchUpgrade.redirectToXref(query, xrefLocale);
        }
    }

    private static async fetchXRefCheck(searchTerm: string, locale: string): Promise<boolean> {
        const url = `/avlmodel/api/singlepart?searchTerm=${encodeURIComponent(searchTerm)}&locale=${locale}`;
        const response = await fetch(url);
        if (response.ok) {
            const json = await response.json();
            return (json && json.result);
        }
        return false;
    }

    private static redirectToXref(searchTerm: string, locale: string): void {
        // returning from redirect to bomcross tool
        if (window.location.hash.indexOf(SearchUpgrade.XREF_HASH) > -1) {
            window.history.replaceState(null, null, Util.removeHashParam(window.location.href, 'supplyframe'));
        } else {
            // redirect to bomcross tool
            window.history.pushState(null, null, `${document.location.pathname}${location.hash}&${SearchUpgrade.XREF_HASH}`);
            window.location.assign(`/cross-reference-search/${locale.toLowerCase()}/singlepart?searchTerm=${searchTerm}`);
        }
    }

    /** Set or update the value of a given queryParam with a specified value.
     *
     * @param paramName - name of the query parameter to be added or updated
     * @param newParamValue - value to set or update the new queryParam to
     */
    private static setOrUpdateQueryParamWithNewValue(paramName: string, newParamValue: string): void {
        if (!paramName || !newParamValue) {
            return;
        }

        const url = document.location.href;
        const currentParamValue = Util.getQueryParam(url, paramName);

        if (!currentParamValue || currentParamValue.localeCompare(newParamValue)) {
            const newUrl = Util.setQueryParam(url, paramName, newParamValue);
            window.history.replaceState(null, null, newUrl);
        }
    }

    private static managePagerVisibility() {
        const paginationState = SearchUpgrade.headlessEngine.state?.pagination;
        const pagerSection = document.querySelector(`atomic-layout-section[section="pagination"]`) as HTMLElement;
        if (paginationState?.totalCountFiltered <= paginationState?.numberOfResults) {
            pagerSection.style.display = 'none';
        } else {
            pagerSection.style.display = '';
        }
    }

    private static updateReflangLinksWithSearchTerm(searchTerm: string) {
        if (searchTerm?.length > 0) {

            const canonical = document.querySelector(`link[rel="canonical"]`);
            let canonicalHref = canonical?.getAttribute('href');
            if (canonicalHref) {
                canonicalHref = Util.setHashParam(canonicalHref, 'q', searchTerm)
                canonical.setAttribute('href', canonicalHref);
            }

            for (let link of document.querySelectorAll(`link[rel="alternate"]`)) {
                let linkHref = link.getAttribute('href');
                linkHref = Util.setHashParam(linkHref, 'q', searchTerm);
                link.setAttribute('href', linkHref);
            };
        }
    }

    private static enableGDPRCompliance(): boolean {
        // parse CONSENTMGR cookie
        const consentCookie: string = Util.getCookie('CONSENTMGR');
        let claims: any = {};
        const claimList: string[] = consentCookie.split('|');
        claimList.forEach(function (claim) {
            const parts: string[] = claim.split(':');
            if (parts.length == 2) {
                claims[parts[0]] = parts[1];
            }
        });
        // determine consent
        let consent: boolean = false;
        if (claims.consent && claims.consent == 'true') {
            consent = true;
            if (claims.c1 && claims.c1 !== '1') {
                consent = false;
            }
        }
        return consent;
    }

    /**
     * Add metrics attributes to result list links
     */
    private static async addResultListMetrics(state: ResultListState) {
        if (!state?.results?.length) {
            return;
        }
        setTimeout(async () => {
            let resultList = document.querySelector('atomic-result-list[data-atomic-rendered="true"]');
            let results = resultList?.shadowRoot?.querySelectorAll('atomic-result');
            let coveoComponentsRendered: boolean = results?.length > 0
                && ((results[0].shadowRoot?.querySelector('atomic-result-link[data-atomic-rendered="true"] a'))?.textContent === state.results[0].title)
                && ((results[0].shadowRoot?.querySelector('atomic-result-printable-uri[data-atomic-rendered="true"] a'))?.textContent === state.results[0].printableUri)
                && ((results[results.length - 1].shadowRoot?.querySelector('atomic-result-link[data-atomic-rendered="true"] a'))?.textContent === state.results[results.length - 1].title)
                && ((results[results.length - 1].shadowRoot?.querySelector('atomic-result-printable-uri[data-atomic-rendered="true"] a'))?.textContent === state.results[results.length - 1].printableUri);

            if (!coveoComponentsRendered) {
                await Util.waitForTrue(() => {
                    resultList = document.querySelector('atomic-result-list[data-atomic-rendered="true"]');
                    results = resultList?.shadowRoot?.querySelectorAll('atomic-result');

                    coveoComponentsRendered = results?.length > 0
                        && ((results[0].shadowRoot?.querySelector('atomic-result-link[data-atomic-rendered="true"] a'))?.textContent === state.results[0].title)
                        && ((results[0].shadowRoot?.querySelector('atomic-result-printable-uri[data-atomic-rendered="true"] a'))?.textContent === state.results[0].printableUri)
                        && ((results[results.length - 1].shadowRoot?.querySelector('atomic-result-link[data-atomic-rendered="true"] a'))?.textContent === state.results[results.length - 1].title)
                        && ((results[results.length - 1].shadowRoot?.querySelector('atomic-result-printable-uri[data-atomic-rendered="true"] a'))?.textContent === state.results[results.length - 1].printableUri);

                    return (coveoComponentsRendered);
                }, 100, 300).catch(() => {
                    return Promise.resolve();
                });
            }
            let idx = 0;

            state.results.forEach((result: Result) => {
                const resultEl = results[idx];
                if (resultEl) {

                    // add mousedown listener on results links to generate metrics calls
                    const titleLinkEl = resultEl.shadowRoot?.querySelector('atomic-result-link a');
                    const printableUriEl = resultEl.shadowRoot?.querySelector('atomic-result-printable-uri a');
                    const docTypeEn = (result.raw as any)['documenttype_english'] ?? 'unknown doctype';
                    const linkUri = result.clickUri;

                    printableUriEl?.addEventListener('mousedown', () => {
                        const linkText = result.printableUri;
                        const eventName = 'onsite search interaction';
                        const eventAction = 'results link click';
                        const eventLabel = `${docTypeEn}|${linkText}`;
                        SearchUpgrade.makeCustomLinkClickAnalyticsCall(eventName, eventAction, eventLabel, linkText, linkUri);
                    });
                    titleLinkEl?.addEventListener('mousedown', () => {
                        const linkText = result.title;
                        const eventName = 'onsite search interaction';
                        const eventAction = 'results link click';
                        const eventLabel = `${docTypeEn}|${linkText}`;
                        SearchUpgrade.makeCustomLinkClickAnalyticsCall(eventName, eventAction, eventLabel, linkText, linkUri);
                    });

                }
                idx++;
            });
        }, 500);

    }

    /**
     * Add metrics for Smart Snippet links
     */
    private static addSmartSnippetsMetrics(): void {
        setTimeout(() => {
            // Smart Snippets
            const smartSnippetEl = document.querySelector('atomic-smart-snippet');
            if (smartSnippetEl) {
                smartSnippetEl.shadowRoot?.querySelectorAll('a[part="source-url"]').forEach(link => {
                    link.setAttribute('href', Util.addQueryParams(link.getAttribute('href'), `usecase=smartsnip`));
                    link.setAttribute('data-navtitle', 'full link');
                });
                smartSnippetEl.shadowRoot?.querySelectorAll('a[part="source-title"]').forEach(title => {
                    title.setAttribute('href', Util.addQueryParams(title.getAttribute('href'), `usecase=smartsnip`));
                    title.setAttribute('data-navtitle', `link text - ${title.getAttribute('title')}`);
                });

                // Smart Snippets Feedback Banner
                smartSnippetEl.shadowRoot?.querySelector('div[part="feedback-banner"]')?.setAttribute('data-lid', 'smart snippet feedback banner');
            }

            // Smart Snippets Suggestions
            const smartSnippetSuggestEl = document.querySelector('atomic-smart-snippet-suggestions');
            if (smartSnippetSuggestEl) {
                smartSnippetSuggestEl.shadowRoot.querySelectorAll('li[part^="question-answer-"]').forEach(suggestion => {
                    suggestion.classList.add('ga-track');
                    const button = suggestion.querySelector('button');
                    const expanded = suggestion.querySelector('button')?.getAttribute('aria-expanded');
                    suggestion.setAttribute('data-navtitle', expanded === 'true'
                        ? 'collapse'
                        : 'expand');
                    if (expanded === 'true') {
                        const link = suggestion.querySelector('atomic-smart-snippet-source')?.querySelector('a[part="source-url"]');
                        link?.setAttribute('href', Util.addQueryParams(link.getAttribute('href'), `usecase=smartsuggest`));
                        link?.setAttribute('data-navtitle', 'full link');

                        const title = suggestion.querySelector('atomic-smart-snippet-source')?.querySelector('a[part="source-title"]');
                        title?.setAttribute('href', Util.addQueryParams(title.getAttribute('href'), `usecase=smartsuggest`));
                        title?.setAttribute('data-navtitle', `link text - ${title.getAttribute('title')}`);
                    }
                    button.addEventListener('click', (e) => {
                        if (e.target !== button) {
                            return;
                        }
                        // allow dom render to complete
                        setTimeout(() => {
                            const expanded = suggestion.querySelector('button')?.getAttribute('aria-expanded');
                            suggestion.setAttribute('data-navtitle', expanded === 'true'
                                ? 'collapse'
                                : 'expand');
                            if (expanded === 'true') {
                                const link = suggestion.querySelector('atomic-smart-snippet-source')?.querySelector('a[part="source-url"]');
                                link?.setAttribute('href', Util.addQueryParams(link?.getAttribute('href'), `usecase=smartsuggest`));
                                link?.setAttribute('data-navtitle', 'full link');

                                const title = suggestion.querySelector('atomic-smart-snippet-source')?.querySelector('a[part="source-title"]');
                                title?.setAttribute('href', Util.addQueryParams(title?.getAttribute('href'), `usecase=smartsuggest`));
                                title?.setAttribute('data-navtitle', `link text - ${title?.getAttribute('title')}`);
                            }
                        }, 300);
                    });
                });
            }
        }, 300);
    }

    private static enqueueAnalyticsCall(eventName: string, eventDetail: string, eventLabel: string = undefined, retryCount: number = 0) {
        this.analyticsQueue.push({
            eventName: eventName,
            eventDetail: eventDetail,
            eventLabel: eventLabel,
            retryCount: retryCount
        });
    }

    private static dequeueAnalyticsCalls() {
        while (this.analyticsQueue.length) {
            const analyticsDetail = this.analyticsQueue.pop();
            this.makeAnalyticsCall(analyticsDetail.eventName, analyticsDetail.eventDetail, analyticsDetail.eventLabel, analyticsDetail.retryCount);
        }
    }

    private static makeAnalyticsCall(eventName: string, eventDetail: string, eventLabel: string = undefined, retryCount: number = 0) {
        if (typeof (window as any)._tiAnalyticsTrack !== 'undefined') {
            (window as any)._tiAnalyticsTrack(eventName, eventDetail, eventLabel);
        } else if (retryCount < this.METRICS_MAX_RETRY_COUNT) {
            retryCount++;
            setTimeout(() => {
                SearchUpgrade.makeAnalyticsCall(eventName, eventDetail, eventLabel, retryCount);
            }, this.METRICS_RETRY_WAIT_TIME);
        }
    }

    private static makeCustomLinkClickAnalyticsCall(eventName: string, eventDetail: string, eventLabel: string, linkText: string, linkUri: string, retryCount: number = 0) {
        if (typeof (window as any)._tiAnalyticsTrack !== 'undefined') {
            (window as any)._tiAnalyticsTrack(eventName, eventDetail, eventLabel, linkText, linkUri);
        } else if (retryCount < this.METRICS_MAX_RETRY_COUNT) {
            retryCount++;
            setTimeout(() => {
                SearchUpgrade.makeCustomLinkClickAnalyticsCall(eventName, eventDetail, eventLabel, linkText, linkUri, retryCount);
            }, this.METRICS_RETRY_WAIT_TIME);
        }
    }

    private static addCoveoCustomMetricsListeners(): void {
        const opnSnapshotTag = document.querySelector('ti-opn-snapshot') as HTMLTiOpnSnapshotElement;
        const toolSnapshotTag = document.querySelector('ti-tool-snapshot') as HTMLTiToolSnapshotElement;
        const gpnSnapshotTag = document.querySelector('ti-gpn-snapshot') as HTMLTiGpnSnapshotElement;
        const partMatchesSnapshotTag = document.querySelector('ti-part-matches') as HTMLTiPartMatchesElement;
        const productFamilySnapshotTag = document.querySelector('ti-product-family') as HTMLTiProductFamilyElement;

        opnSnapshotTag.addEventListener('click', () => {
            SearchUpgrade.fireCustomCoveoMetrics('snapshot_interaction', 'opn');
        });
        toolSnapshotTag.addEventListener('click', () => {
            SearchUpgrade.fireCustomCoveoMetrics('snapshot_interaction', 'tool');
        });
        gpnSnapshotTag.addEventListener('click', () => {
            SearchUpgrade.fireCustomCoveoMetrics('snapshot_interaction', 'gpn');
        });
        partMatchesSnapshotTag.addEventListener('click', () => {
            SearchUpgrade.fireCustomCoveoMetrics('snapshot_interaction', 'partMatches');
        });
        productFamilySnapshotTag.addEventListener('click', () => {
            SearchUpgrade.fireCustomCoveoMetrics('snapshot_interaction', 'productFamily');
        });
    }

    private static fireCustomCoveoMetrics(event: string, type: string) {

        const { logCustomEvent } = loadGenericAnalyticsActions(SearchUpgrade.headlessEngine);
        const payload = {
            evt: event,
            type: type
        };
        SearchUpgrade.headlessEngine.dispatch(logCustomEvent(payload));
    }

    private static addUseCaseListeners(): void {
        const opnSnapshotTag = document.querySelector('ti-opn-snapshot') as HTMLTiOpnSnapshotElement;
        const toolSnapshotTag = document.querySelector('ti-tool-snapshot') as HTMLTiToolSnapshotElement;
        const gpnSnapshotTag = document.querySelector('ti-gpn-snapshot') as HTMLTiGpnSnapshotElement;
        const partMatchesSnapshotTag = document.querySelector('ti-part-matches') as HTMLTiPartMatchesElement;
        const eventName = 'search use case';

        opnSnapshotTag?.addEventListener('componentRendered', (e: CustomEvent) => {
            if (opnSnapshotTag.tagName == e.detail?.tag) {
                SearchUpgrade.enqueueAnalyticsCall(eventName, 'OPN');
            }
        });
        toolSnapshotTag?.addEventListener('componentRendered', (e: CustomEvent) => {
            const toolGroup: string = e.detail.toolGroup;
            if (toolSnapshotTag.tagName == e.detail?.tag) {
                SearchUpgrade.enqueueAnalyticsCall(eventName, toolGroup);
            }
        });
        gpnSnapshotTag?.addEventListener('componentRendered', (e: CustomEvent) => {
            if (gpnSnapshotTag.tagName == e.detail?.tag) {
                SearchUpgrade.enqueueAnalyticsCall(eventName, 'GPN');
            }
        });
        partMatchesSnapshotTag?.addEventListener('componentRendered', (e: CustomEvent) => {
            if (partMatchesSnapshotTag.tagName == e.detail?.tag) {
                SearchUpgrade.enqueueAnalyticsCall(eventName, 'partmatches');
            }
        });
    }

    private static readonly SORT_METRICS = {
        'relevancy': 'Relevance',
        'date descending': 'Most recent'
    }

    /**
     * Close the mobile search navbar if it is open
     */
    private static closeMobileSearchBox() {
        const closeSearchIcon = document.querySelector('li.is-open > a') as HTMLAnchorElement;
        if (closeSearchIcon) {
            closeSearchIcon.click();
        }

    }

    /**
     * Synchronize the selected filters from the preFilter query parameter
     *
     * The preFilter queryParameter must follow the format prefilter=facetName_facetValues;facet2Name_facet2Values;{repeating}
     * Or the custom preFilter value prefilter=latestvideo, which will select all values under the 'video' filter facet
     *
     */
    private static async handlePrefilter(): Promise<void> {
        const filter = Util.getUrlParameter('preFilter');
        if (!filter) {
            return;
        }
        const oldHash = window.location.hash;
        let newFragment = oldHash;
        // Handle custom preFilter case 'latestvideo'
        if ('latestvideo' === filter?.toLowerCase()) {
            let children = [];
            let facetValues = document.querySelector('[facet-id="videos"]')?.shadowRoot?.querySelectorAll('[part="value-label"]');
            await Util.waitForTrue(() => {
                facetValues = document.querySelector('[facet-id="videos"]')?.shadowRoot?.querySelectorAll('[part="value-label"]');
                return facetValues?.length > 0;
            }, 10, 100).catch(() => {
                return Promise.resolve();
            });
            facetValues.forEach((node: any) => children.push(node.title));
            if (children.length) {
                newFragment = Util.setHashParam(newFragment, 'f-videos', `${children.join(',')}`);
            } else {
                const videoFacetOptions = SearchUpgrade.headlessEngine.state.facetSet['videos']?.request?.currentValues?.map(val => val.value).join(',');
                if (videoFacetOptions) {
                    newFragment = Util.setHashParam(newFragment, 'f-videos', videoFacetOptions);
                }
            }
            newFragment = Util.setHashParam(newFragment, 'sortCriteria', 'date descending');
            if (oldHash.localeCompare(newFragment) !== 0) {
                window.location.hash = newFragment;
                history.replaceState(null, null, window.location.href);
                return;
            }
        } else {
            const preFilterList = filter.split(';');
            preFilterList.forEach(preFilterItem => {
                const preFilter = preFilterItem.split('_');
                const facetName = decodeURIComponent(preFilter[0]);
                const facetValues = preFilter[1];

                if (facetName === 'designResourceProvider') {
                    SearchUpgrade.referralSource = 'partner';
                }

                if (facetValues.length > 0) {
                    //check for a standard facet, else use the hierarchical facet
                    if (SearchUpgrade.headlessEngine.state.facetSet[facetName]) {
                        newFragment = Util.setHashParam(newFragment, `f-${facetName}`, facetValues);
                    } else if (SearchUpgrade.headlessEngine.state.categoryFacetSet[facetName]) {
                        newFragment = Util.setHashParam(newFragment, `cf-${facetName}`, facetValues);
                    }

                    if (oldHash.localeCompare(newFragment) !== 0) {
                        window.location.hash = newFragment;
                        history.replaceState(null, null, window.location.href);
                        return;
                    }
                }
            });
        }
    }

    private static async removePrefilter() {
        SearchUpgrade.referralSource = null;
        const url = document.location.href;
        const preFilter = Util.getQueryParam(url, 'preFilter');
        if (preFilter) {
            const newUrl = Util.removeQueryParam(url, 'preFilter');
            window.history.replaceState(null, null, newUrl);
        }
    }

    private static trackSearchMetrics(searchTerm: string, nullSearch: boolean = false): void {
        const searchEvent = nullSearch ? 'null search' : 'search';
        const contentGroup = 'CONTENT+CATEGORY';
        const pageName = 'Universal Search:everything';
        SearchUpgrade.makeOctMetricsCall(searchTerm, searchEvent, pageName, contentGroup);
    }

    private static makeOctMetricsCall(searchTerm: string, nullSearch: string, pgt: string, tiContentGroup: string, retryCount: number = 0) {
        if (typeof (window as any)._oct_metrics !== 'undefined') {
            (window as any)._oct_metrics(searchTerm, nullSearch, pgt, tiContentGroup);
        } else if (retryCount < this.METRICS_MAX_RETRY_COUNT) {
            retryCount++;
            setTimeout(function () {
                SearchUpgrade.makeOctMetricsCall(searchTerm, nullSearch, pgt, tiContentGroup, retryCount);
            }, this.METRICS_RETRY_WAIT_TIME);
        }
    }
}
